import type { Point, Tube, YarnPoint } from "./needleCycle";

// Keeps the yarn clear of itself and of the needles, and checks the drawing against how plain jersey
// goes together. Its face is toward the needles: a loop's legs pass on the needles' side of the head
// of the loop below it on the same needle, and its sinker loops beyond that loop's legs.

// Along a loop, 0 at its head and ±π at its sinker loops, its parts ease from one to the next so
// a loop pulled out of shape still sorts the way it lies: its head, its legs, and its sinker loops.
// A loop still drawn tight by its needle has legs only as far as where they pass through the loop
// below; past there, the yarn runs on to the sinkers.
const HEAD_END: [number, number] = [0.6, 0.95];
const LEGS_END: [number, number] = [1.9, 2.5];
const TAUT_LEGS_END: [number, number] = [1.35, 1.55];
const LOWER_LEGS_END: [number, number] = [2.3, 2.7];

const ease = ([from, to]: readonly [number, number], value: number) => {
  const t = Math.min(Math.max((value - from) / (to - from), 0), 1);
  return t * t * (3 - 2 * t);
};

// Stretches of the strand closer than this along it are neighbours, not crossings.
const NEIGHBOURS = 0.4;

const CELL = 0.25;

type Closest = { s: number; t: number; distance: number; from: Point; to: Point };

const closest = (p0: Point, p1: Point, q0: Point, q1: Point): Closest => {
  const d1 = { x: p1.x - p0.x, y: p1.y - p0.y, z: p1.z - p0.z };
  const d2 = { x: q1.x - q0.x, y: q1.y - q0.y, z: q1.z - q0.z };
  const r = { x: p0.x - q0.x, y: p0.y - q0.y, z: p0.z - q0.z };
  const dot = (a: Point, b: Point) => a.x * b.x + a.y * b.y + a.z * b.z;
  const a = dot(d1, d1);
  const e = dot(d2, d2);
  const f = dot(d2, r);
  const c = dot(d1, r);
  const b = dot(d1, d2);
  const clamp = (value: number) => Math.min(Math.max(value, 0), 1);
  const denominator = a * e - b * b;
  let s = a > 1e-12 && denominator > 1e-12 ? clamp((b * f - c * e) / denominator) : 0;
  let t = e > 1e-12 ? (b * s + f) / e : 0;
  if (t < 0) {
    t = 0;
    s = a > 1e-12 ? clamp(-c / a) : 0;
  } else if (t > 1) {
    t = 1;
    s = a > 1e-12 ? clamp((b - c) / a) : 0;
  }
  const from = { x: p0.x + d1.x * s, y: p0.y + d1.y * s, z: p0.z + d1.z * s };
  const to = { x: q0.x + d2.x * t, y: q0.y + d2.y * t, z: q0.z + d2.z * t };
  return { s, t, distance: Math.hypot(from.x - to.x, from.y - to.y, from.z - to.z), from, to };
};

// +1 if the yarn at `a` must lie beyond the yarn at `b`, further from the needles, -1 if nearer
// them, 0 if either will do.
export const ruled = (a: YarnPoint, b: YarnPoint) => {
  if (
    a.needle < 0 ||
    a.needle !== b.needle ||
    Math.abs(a.course - b.course) !== 1 ||
    !Number.isFinite(a.along) ||
    !Number.isFinite(b.along)
  ) {
    return 0;
  }
  const aAbove = a.course > b.course;
  const above = aAbove ? a : b;
  const below = aAbove ? b : a;
  const legsEnd: [number, number] = [
    LEGS_END[0] + (TAUT_LEGS_END[0] - LEGS_END[0]) * above.taut,
    LEGS_END[1] + (TAUT_LEGS_END[1] - LEGS_END[1]) * above.taut,
  ];
  const upper = Math.abs(above.along);
  const lower = Math.abs(below.along);
  const upperHead = 1 - ease(HEAD_END, upper);
  const upperSinker = ease(legsEnd, upper);
  const upperLegs = 1 - upperHead - upperSinker;
  const lowerHead = 1 - ease(HEAD_END, lower);
  const lowerLegs = (1 - lowerHead) * (1 - ease(LOWER_LEGS_END, lower));
  // The new loop goes through the old one toward the needles: its legs pass nearer them than the old
  // loop's head and beyond its legs, and its sinker loops beyond the old loop's legs too. Until the
  // old loop has turned over round the new one's legs, they still pass beyond its head, inside it;
  // and where the two heads pass, which is nearer changes as the old loop comes off the hook. There
  // the drawing already has them the right way round.
  const score = upperLegs * lowerHead * above.turned - (upperLegs + upperSinker) * lowerLegs;
  if (Math.abs(score) < 0.35) return 0;
  return score > 0 === aAbove ? -1 : 1;
};

// Where, seen face on, one stretch of yarn crosses another on the wrong side of it: for checking
// the drawing against how jersey goes together.
export const wrongCrossings = (yarn: readonly YarnPoint[]) => {
  const found: Point[] = [];
  const arc = [0];
  for (let index = 1; index < yarn.length; index += 1) {
    const previous = yarn[index - 1];
    const point = yarn[index];
    arc.push(arc[index - 1] + Math.hypot(point.x - previous.x, point.y - previous.y, point.z - previous.z));
  }
  for (let i = 0; i + 1 < yarn.length; i += 1) {
    const a0 = yarn[i];
    const a1 = yarn[i + 1];
    for (let j = i + 2; j + 1 < yarn.length; j += 1) {
      if (arc[j] - arc[i + 1] < NEIGHBOURS) continue;
      const b0 = yarn[j];
      const b1 = yarn[j + 1];
      // where the two segments cross in x and y, if they do
      const denominator = (a1.x - a0.x) * (b1.y - b0.y) - (a1.y - a0.y) * (b1.x - b0.x);
      if (Math.abs(denominator) < 1e-12) continue;
      const s = ((b0.x - a0.x) * (b1.y - b0.y) - (b0.y - a0.y) * (b1.x - b0.x)) / denominator;
      const t = ((b0.x - a0.x) * (a1.y - a0.y) - (b0.y - a0.y) * (a1.x - a0.x)) / denominator;
      if (s < 0 || s > 1 || t < 0 || t > 1) continue;
      const rule = ruled(s < 0.5 ? a0 : a1, t < 0.5 ? b0 : b1);
      if (rule === 0) continue;
      const za = a0.z + (a1.z - a0.z) * s;
      const zb = b0.z + (b1.z - b0.z) * t;
      if ((za - zb) * rule < 0)
        found.push({ x: a0.x + (a1.x - a0.x) * s, y: a0.y + (a1.y - a0.y) * s, z: (za + zb) / 2 });
    }
  }
  return found;
};

// Which loops do not hang on the loop they were pulled through: seen face on, each leg of a loop
// in the fabric passes nearer the needles than that loop's head, so a loop missing either crossing
// has slipped past it rather than through it. `through(needle, course)` says whether that loop
// should lie so yet.
export const missingLoops = (yarn: readonly YarnPoint[], through: (needle: number, course: number) => boolean) => {
  const found: { needle: number; course: number }[] = [];
  const loops = new Map<string, { legs: [YarnPoint, YarnPoint][]; heads: [YarnPoint, YarnPoint][] }>();
  const entry = (needle: number, course: number) => {
    const key = `${needle}:${course}`;
    let value = loops.get(key);
    if (!value) loops.set(key, (value = { legs: [], heads: [] }));
    return value;
  };
  for (let index = 0; index + 1 < yarn.length; index += 1) {
    const a = yarn[index];
    const b = yarn[index + 1];
    if (a.needle < 0 || a.needle !== b.needle || a.course !== b.course || !Number.isFinite(a.along)) continue;
    const along = Math.abs(a.along);
    if (along > 0.4 && along < 2.6) entry(a.needle, a.course).legs.push([a, b]);
    if (along < 0.95) entry(a.needle, a.course).heads.push([a, b]);
  }
  for (const [key, { legs }] of loops) {
    const [needle, course] = key.split(":").map(Number);
    const below = loops.get(`${needle}:${course - 1}`);
    if (!below || !through(needle, course)) continue;
    const sides = new Set<number>();
    for (const [a0, a1] of legs) {
      for (const [b0, b1] of below.heads) {
        const denominator = (a1.x - a0.x) * (b1.y - b0.y) - (a1.y - a0.y) * (b1.x - b0.x);
        if (Math.abs(denominator) < 1e-12) continue;
        const s = ((b0.x - a0.x) * (b1.y - b0.y) - (b0.y - a0.y) * (b1.x - b0.x)) / denominator;
        const t = ((b0.x - a0.x) * (a1.y - a0.y) - (b0.y - a0.y) * (a1.x - a0.x)) / denominator;
        if (s < 0 || s > 1 || t < 0 || t > 1) continue;
        const za = a0.z + (a1.z - a0.z) * s;
        const zb = b0.z + (b1.z - b0.z) * t;
        if (za < zb) sides.add(Math.sign(a0.along));
      }
    }
    if (sides.size < 2) found.push({ needle, course });
  }
  return found;
};

// Where the yarn still runs into itself or into a needle: for checking the drawing.
export const clashes = (yarn: readonly YarnPoint[], obstacles: readonly Tube[], radius: number, tolerance = 0.004) => {
  const found: Point[] = [];
  const arc = [0];
  for (let index = 1; index < yarn.length; index += 1) {
    const previous = yarn[index - 1];
    const point = yarn[index];
    arc.push(arc[index - 1] + Math.hypot(point.x - previous.x, point.y - previous.y, point.z - previous.z));
  }
  for (let i = 0; i + 1 < yarn.length; i += 1) {
    for (let j = i + 2; j + 1 < yarn.length; j += 1) {
      if (arc[j] - arc[i + 1] < NEIGHBOURS) continue;
      if (Math.abs(yarn[i].x - yarn[j].x) > 0.5 || Math.abs(yarn[i].y - yarn[j].y) > 0.5) continue;
      const hit = closest(yarn[i], yarn[i + 1], yarn[j], yarn[j + 1]);
      if (hit.distance < 2 * radius - tolerance) {
        found.push({ x: (hit.from.x + hit.to.x) / 2, y: (hit.from.y + hit.to.y) / 2, z: (hit.from.z + hit.to.z) / 2 });
      }
    }
  }
  for (const tube of obstacles) {
    for (let k = 0; k + 1 < tube.points.length; k += 1) {
      for (let i = 0; i + 1 < yarn.length - 3; i += 1) {
        if (Math.abs(yarn[i].x - tube.points[k].x) > 0.5) continue;
        const hit = closest(yarn[i], yarn[i + 1], tube.points[k], tube.points[k + 1]);
        if (hit.distance < radius + tube.radius - tolerance) found.push(hit.from);
      }
    }
  }
  return found;
};

// Moves each point of the yarn off any needle or carrier it runs into, out to where it just clears it.
export const clearOf = (
  yarn: readonly YarnPoint[],
  obstacles: readonly Tube[],
  radius: number,
  passes = 2,
): YarnPoint[] => {
  const points = yarn.map((point) => ({ ...point }));
  for (let pass = 0; pass < passes; pass += 1) {
    for (const tube of obstacles) {
      const clear = radius + tube.radius + 0.004;
      for (let k = 0; k + 1 < tube.points.length; k += 1) {
        const q0 = tube.points[k];
        const q1 = tube.points[k + 1];
        const low = Math.min(q0.x, q1.x) - clear;
        const high = Math.max(q0.x, q1.x) + clear;
        const dx = q1.x - q0.x;
        const dy = q1.y - q0.y;
        const dz = q1.z - q0.z;
        const span = dx * dx + dy * dy + dz * dz;
        points.forEach((point, index) => {
          if (point.x < low || point.x > high) return;
          const t =
            span > 1e-12
              ? Math.min(Math.max(((point.x - q0.x) * dx + (point.y - q0.y) * dy + (point.z - q0.z) * dz) / span, 0), 1)
              : 0;
          const ox = point.x - (q0.x + dx * t);
          const oy = point.y - (q0.y + dy * t);
          const oz = point.z - (q0.z + dz * t);
          const distance = Math.hypot(ox, oy, oz);
          if (distance >= clear || distance < 1e-9) return;
          const scale = clear / distance;
          points[index] = {
            ...point,
            x: point.x + ox * (scale - 1),
            y: point.y + oy * (scale - 1),
            z: point.z + oz * (scale - 1),
          };
        });
      }
    }
  }
  return points;
};

// Pushes apart stretches of yarn that run into each other: each pair closer than the yarn is thick
// moves straight apart, half each, spread over the ends of the two stretches. Nothing else moves, and
// how far things move grows smoothly from nothing as they touch, so the yarn keeps the shape it was
// drawn with and moves smoothly from one frame to the next.
export const separate = (yarn: readonly YarnPoint[], radius: number, passes = 2): YarnPoint[] => {
  const count = yarn.length;
  const points = yarn.map((point) => ({ ...point }));
  const arc = [0];
  for (let index = 1; index < count; index += 1) {
    const previous = yarn[index - 1];
    const point = yarn[index];
    arc.push(arc[index - 1] + Math.hypot(point.x - previous.x, point.y - previous.y, point.z - previous.z));
  }
  const cellOf = (x: number, y: number) => `${Math.floor(x / CELL)},${Math.floor(y / CELL)}`;
  for (let pass = 0; pass < passes; pass += 1) {
    const grid = new Map<string, number[]>();
    for (let index = 0; index + 1 < count; index += 1) {
      const key = cellOf((points[index].x + points[index + 1].x) / 2, (points[index].y + points[index + 1].y) / 2);
      const cell = grid.get(key);
      if (cell) cell.push(index);
      else grid.set(key, [index]);
    }
    const shift = new Float64Array(count * 3);
    const weight = new Float64Array(count);
    const push = (index: number, s: number, x: number, y: number, z: number) => {
      [
        [index, 1 - s],
        [index + 1, s],
      ].forEach(([at, share]) => {
        shift[at * 3] += x * share;
        shift[at * 3 + 1] += y * share;
        shift[at * 3 + 2] += z * share;
        weight[at] += share;
      });
    };
    for (let i = 0; i + 1 < count; i += 1) {
      const a0 = points[i];
      const a1 = points[i + 1];
      const cx = Math.floor((a0.x + a1.x) / 2 / CELL);
      const cy = Math.floor((a0.y + a1.y) / 2 / CELL);
      for (let dx = -1; dx <= 1; dx += 1) {
        for (let dy = -1; dy <= 1; dy += 1) {
          for (const j of grid.get(`${cx + dx},${cy + dy}`) ?? []) {
            if (j <= i + 1 || arc[j] - arc[i + 1] < NEIGHBOURS) continue;
            const hit = closest(a0, a1, points[j], points[j + 1]);
            if (hit.distance >= 2 * radius || hit.distance < 1e-6) continue;
            const apart = (2 * radius - hit.distance) / 2 / hit.distance;
            const [x, y, z] = [hit.from.x - hit.to.x, hit.from.y - hit.to.y, hit.from.z - hit.to.z];
            push(i, hit.s, x * apart, y * apart, z * apart);
            push(j, hit.t, -x * apart, -y * apart, -z * apart);
          }
        }
      }
    }
    for (let index = 0; index < count; index += 1) {
      if (!weight[index]) continue;
      const point = points[index];
      points[index] = {
        ...point,
        x: point.x + shift[index * 3] / weight[index],
        y: point.y + shift[index * 3 + 1] / weight[index],
        z: point.z + shift[index * 3 + 2] / weight[index],
      };
    }
  }
  return points;
};
